/* eslint-disable react/jsx-curly-newline */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Logger } from '@saviynt/common';
import {
  AlertBannerProvider,
  Badge,
  Box,
  Button,
  ButtonSelect,
  Icon,
  InputField,
  Loader,
  StackingAlertBanner,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import useActivationBanner from '../../hooks/use-activationbanner';
import useIgnoreBanner from '../../hooks/use-ignorebanner';
import { useInfiniteRoles } from '../../hooks/use-infiniteroles';
import { type RootState } from '../../services/types';
import { DirectionalArrow } from '../../shared';
import { useStickyOffset } from '../../shared/hooks';
import { useInvertedLocalize } from '../../utils/localization';
import { KpiTiles } from '../KpiTiles';

import { DashboardFilterPills } from './DashboardFilterPills';
import { useDashboardFilters, useSearchHandlers } from './hooks';
import invertedMessages from './messages';
import { RoleCard } from './RoleCard';

import './RolesDashboard.css';

type CardKinds = 'fluid' | 'compact';
// card kind for dashboard layout (compact shows multiple cards per row in grid)
const CARD_KIND = 'compact' as CardKinds;

// page size per card kind: 6 for 'compact' ensures complete rows at 3 per row, 2 per row, and 1 per row
// 5 for fluid maintains existing behavior for full-width cards
// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- intentional: create const instead of magic numbers
const PAGE_SIZE_API = CARD_KIND === 'compact' ? 6 : 5;

function RolesDashboard(): JSX.Element {
  // localization setup
  const l = useInvertedLocalize(invertedMessages);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    applications: [] as string[],
    entitlements: [] as string[],
  });
  const history = useHistory();

  const isMenuPinned = useSelector(
    (state: RootState) => state.user.isMenuPinned
  );

  // Check if any filters are active for badge display
  const hasActiveFilters = useMemo(
    () =>
      activeFilters.applications.length > 0 ||
      activeFilters.entitlements.length > 0,
    [activeFilters]
  );

  // Zero roles state tracking
  const [hasEverShownRoles, setHasEverShownRoles] = useState(false);

  // activation banner hook for role activation banners
  const { successBanner, errorBanner } = useActivationBanner();

  // ignore banner hook for role ignore banners
  const { ignoreBanner } = useIgnoreBanner();

  // Use search handlers hook
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const {
    searchInput,
    searchValue,
    handleSearchInputChange,
    handleSearchKeyDown,
    handleSearchClear,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
  } = useSearchHandlers();

  // Height preservation for smooth loading transitions
  const cardsRef = useRef<HTMLDivElement>(null);
  const [preservedHeight, setPreservedHeight] = useState<number>();

  // Scroll tracking for infinite scroll
  const lastScrollTopRef = useRef(0);

  // Sticky header measurement for dynamic positioning
  const headerRef = useRef<HTMLElement>(null);

  // Measure dashboard header height for controls positioning under it
  useStickyOffset(headerRef.current, {
    cssVarName: '--roles-dashboard-header-height',
  });

  // Fetch filter options
  const { data: filterOptionsData, isLoading: isLoadingFilters } =
    useDashboardFilters();

  // Transform filter options for UI
  const availableFilters = useMemo(
    () => ({
      applications:
        filterOptionsData?.endpointNames.map((name) => ({
          label: name,
          value: name,
        })) ?? [],
      entitlements:
        filterOptionsData?.entitlementValueNames.map((name) => ({
          label: name, // TODO: Map to display names later
          value: name,
        })) ?? [],
    }),
    [filterOptionsData]
  );

  // API request body with filters (pagination handled by infinite query)
  const requestBody = useMemo(
    () => ({
      filters: {
        ...(searchValue && { search: searchValue }),
        ...(activeFilters.applications.length > 0 && {
          endpointName: activeFilters.applications,
        }),
        ...(activeFilters.entitlements.length > 0 && {
          entitlementValueName: activeFilters.entitlements,
        }),
      },
    }),
    [searchValue, activeFilters]
  );

  // Use infinite query for pagination support with dynamic page size based on card kind
  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteRoles(requestBody, PAGE_SIZE_API);

  // Get userAccess from the first page of data
  const userAccess = infiniteData?.pages[0]?.userAccess;

  // Get totalNewObjects from the first page of data
  const newRecommendationsCount = infiniteData?.pages[0]?.totalNewObjects ?? 0;

  // Flatten paginated data from all pages
  const allRoles = useMemo(
    () => infiniteData?.pages.flatMap((page) => page.data) ?? [],
    [infiniteData]
  );

  // Track when roles are successfully loaded
  useEffect(() => {
    if (allRoles.length > 0) {
      setHasEverShownRoles(true);
    }
  }, [allRoles.length]);

  // Zero state detection
  const isInitialZeroRoles =
    !isLoading && !error && allRoles.length === 0 && !hasEverShownRoles;
  const isFilteredZeroRoles =
    !isLoading && !error && allRoles.length === 0 && hasEverShownRoles;

  // Track cards container height for smooth loading transitions
  useEffect(() => {
    if (!isLoading && cardsRef.current) {
      setPreservedHeight(cardsRef.current.offsetHeight);
    }
  }, [isLoading, infiniteData]);

  // Window scroll handler for infinite scroll
  useEffect(() => {
    const handleWindowScroll = () => {
      // Don't trigger infinite scroll in zero state scenarios
      if (isInitialZeroRoles || isFilteredZeroRoles || !hasNextPage) {
        return;
      }

      const { scrollTop, scrollHeight, clientHeight } =
        document.documentElement;
      const isBottom = scrollHeight - scrollTop <= clientHeight + 20; // 20px threshold
      const isScrollingDown = scrollTop > lastScrollTopRef.current;

      if (isBottom && isScrollingDown && !isFetchingNextPage) {
        fetchNextPage().catch((fetchError) => {
          Logger.error('Failed to fetch next page', fetchError);
        });
      }

      lastScrollTopRef.current = scrollTop;
    };

    window.addEventListener('scroll', handleWindowScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleWindowScroll);
  }, [
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isInitialZeroRoles,
    isFilteredZeroRoles,
  ]);

  // Filter handlers
  const handleSetFilter = useCallback(
    (category: 'applications' | 'entitlements', values: string[]) => {
      setActiveFilters((prev) => ({
        ...prev,
        [category]: values,
      }));
    },
    []
  );

  const handleClearAllFilters = useCallback(() => {
    setActiveFilters({
      applications: [],
      entitlements: [],
    });
  }, []);

  const handleViewDetails = (roleId: number) => {
    history.push(`/role-details/${roleId}`);
  };

  // Main content renderer with zero state support
  const renderMainContent = () => {
    if (isLoading) {
      return (
        <Box className='Roles-Dashboard--isLoading'>
          <Loader kind='loop' format='inline' color='brand' size='large' />
        </Box>
      );
    }

    // Handle error state
    if (error) {
      Logger.error('Failed to load roles', error);

      return (
        <Box className='Roles-Dashboard Roles-Dashboard--error'>
          <Icon
            kind='AlertCriticalOutline'
            size='large'
            color='--color-background-critical-bold'
            className='Dashboard-zeroRolesIcon'
          />
          <Typography
            kind='body2-bold'
            color='--color-foreground-neutral-bold'
            className='Dashboard-zeroRolesMessage'>
            {l('Error encountered.')}
          </Typography>
          <Typography kind='body2' color='--color-foreground-neutral-subtle'>
            {l('Check logs and contact your administrator.')}
          </Typography>
        </Box>
      );
    }

    if (isInitialZeroRoles) {
      return (
        <Box className='Roles-Dashboard-initialZeroRoles'>
          <Icon
            kind='AlertInfo'
            size='large'
            color='--color-foreground-info-medium'
            className='Roles-Dashboard-zeroRolesIcon'
          />
          <Typography
            kind='body2-bold'
            color='--color-foreground-neutral-bold'
            className='Roles-Dashboard-zeroRolesMessage'>
            {l('The feature for this data is not set up.')}
          </Typography>

          <Typography kind='body3' color='--color-foreground-neutral-subtle'>
            {l('Try running the Role Recommendations job to generate Roles')}
          </Typography>
          <Button className='Roles-Dashboard-runJobButton'>
            {l('Run Job')} <DirectionalArrow />
          </Button>
        </Box>
      );
    }

    if (isFilteredZeroRoles) {
      return (
        <Box className='Roles-Dashboard-filteredZeroRoles'>
          <Icon
            kind='AlertInfo'
            size='large'
            color='--color-foreground-info-medium'
            className='Roles-Dashboard-zeroRolesIcon'
          />
          <Typography
            kind='body2-bold'
            color='--color-foreground-neutral-bold'
            className='Roles-Dashboard-filteredZeroRolesMessage'>
            {l('Sorry, there are no results for the search query you entered.')}
          </Typography>
        </Box>
      );
    }

    // Normal roles display with infinite scroll
    return (
      <>
        {allRoles.map((role) => (
          <RoleCard
            key={role.recommendedObjectKey}
            role={role}
            onViewDetails={handleViewDetails}
            userAccess={userAccess}
            kind={CARD_KIND}
            className='Roles-Dashboard-card'
          />
        ))}
        {isFetchingNextPage && (
          <Box className='Roles-Dashboard--isLoadingMore'>
            <Loader kind='loop' format='inline' color='brand' size='medium' />
          </Box>
        )}
      </>
    );
  };

  return (
    <>
      <Box className='Roles-Dashboard'>
        <header ref={headerRef} className='Roles-Dashboard-header'>
          <div className='Roles-Dashboard-header-iconTitleWrapper'>
            <Icon kind='Security' size='large' />
            <Typography kind='h1' className='Roles-Dashboard-title'>
              {l('Role Recommendations')}
            </Typography>
          </div>
          <Typography kind='body2' color='--color-foreground-neutral-subtle'>
            {l('Suggested Roles from Intelligent Recommendations')}
          </Typography>
        </header>

        <KpiTiles />
        <Box tag='section' className='Roles-Dashboard-controlsFiltersWrapper'>
          <Box className='Roles-Dashboard-controls'>
            <Box className='Roles-Dashboard-search'>
              <InputField
                name='search'
                placeholder={l('Search all Recommendations')}
                kind='search'
                prefixIcon={
                  <Icon
                    kind='Search'
                    color='--color-foreground-neutral-subtle'
                  />
                }
                value={searchInput}
                setValue={handleSearchInputChange}
                onKeyDown={handleSearchKeyDown}
                resetValue={handleSearchClear}
                size='medium'
              />
            </Box>
            <Box className='Roles-Dashboard-filters'>
              <ButtonSelect
                kind='rectangle'
                label={l('Filter')}
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                isOpen={isFilterOpen}
                prefixIcon={<Icon kind='Filter' />}
                BadgeComp={
                  hasActiveFilters ? (
                    <Badge kind='dot' color='primary' size='small' />
                  ) : null
                }
              />
              {/* for mvp2: <Button kind='outlined'>&lt;/&gt; View: Condition</Button> */}
            </Box>
          </Box>

          <DashboardFilterPills
            isVisible={isFilterOpen}
            activeFilters={activeFilters}
            availableFilters={availableFilters}
            isLoading={isLoadingFilters}
            onSetFilter={handleSetFilter}
            onClearAll={handleClearAllFilters}
          />
        </Box>

        <Box className='Roles-Dashboard-newRecommendationsCount'>
          {(() => {
            const isSearchingOrFiltering = searchValue || hasActiveFilters;
            const totalRoleCount = infiniteData?.pages[0]?.totalCount;

            return (
              Boolean(totalRoleCount) && (
                <Typography kind='h3' color='--color-border-neutral-bold'>
                  {isSearchingOrFiltering
                    ? l('Total Results: {count}', {
                        count: totalRoleCount ?? 0,
                      })
                    : Boolean(newRecommendationsCount) &&
                      l(
                        '{count, plural, one {# New Role Recommendation Found} other {# New Role Recommendations Found}}',
                        {
                          count: newRecommendationsCount,
                        }
                      )}
                </Typography>
              )
            );
          })()}
        </Box>

        <div
          ref={cardsRef}
          style={{
            minHeight: isLoading ? preservedHeight : 'auto',
          }}>
          <Box
            tag='section'
            className={cx(
              'Roles-Dashboard-cards',
              CARD_KIND === 'compact' && 'Roles-Dashboard-cards--compact'
            )}
            role='list'
            aria-label='Role recommendations'>
            {renderMainContent()}
          </Box>
        </div>
      </Box>

      <AlertBannerProvider
        className={cx(
          'Roles-Dashboard-alertBannerProvider',
          isMenuPinned && 'Roles-Dashboard-alertBannerProvider--isMenuPinned'
        )}>
        <StackingAlertBanner
          colorTheme='Success'
          title={l('Congratulations!')}
          description={successBanner.message}
          isVisible={successBanner.isVisible}
          onCancel={successBanner.onClose}
          isColonVisible={false}
          shouldAutoDismiss
        />

        <StackingAlertBanner
          colorTheme='Critical'
          title={l('Oops!')}
          description={errorBanner.message}
          isVisible={errorBanner.isVisible}
          onCancel={errorBanner.onClose}
          isColonVisible={false}
          shouldAutoDismiss={false}
        />

        <StackingAlertBanner
          colorTheme={ignoreBanner.colorTheme}
          title={
            ignoreBanner.colorTheme === 'Success' ? l('Success!') : l('Oops!')
          }
          description={ignoreBanner.message}
          isVisible={ignoreBanner.isVisible}
          onCancel={ignoreBanner.onClose}
          isColonVisible={false}
          shouldAutoDismiss={ignoreBanner.colorTheme === 'Success'}
        />
      </AlertBannerProvider>
    </>
  );
}

export default RolesDashboard;
